import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resellers, type ResellerRow } from '@/api/resellers';
import { PageHeader } from '@/components/PageHeader';
import { DataTable, type Column } from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, ToggleLeft, LogIn, ArrowRight, Check } from 'lucide-react';
import { useResellerContext } from '@/lib/reseller-context-store';
import { useAuth } from '@/lib/auth-store';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';

/**
 * Resellers list — entry point for the super_admin drill-down.
 *
 * Super_admins "enter" a reseller from this page (two-step drill-down):
 * reseller → customer → customer-scoped tooling. The Enter column writes
 * the chosen reseller into the reseller-context store and navigates to
 * /customers, which is then filtered to that reseller's customers.
 *
 * Only super_admin reaches this page (the nav hides it for every other
 * role), so the Enter affordance is always shown.
 */
export function ResellerList() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useQuery({ queryKey: ['resellers'], queryFn: () => resellers.list() });
  const navigate = useNavigate();
  const current = useResellerContext((s) => s.current);
  const enter = useResellerContext((s) => s.enter);
  const user = useAuth((s) => s.user);
  const canEnter = user?.role === 'super_admin';

  function handleEnter(row: ResellerRow) {
    // Persist the four-digit reseller_number so the scoped URL helper
    // can build `/reseller/{N}/customer/{N}/...` paths after entering.
    enter({ id: row.id, name: row.name, resellerNumber: row.reseller_number });
    navigate('/customers');
  }

  const columns: Column<ResellerRow>[] = [
    {
      header: '#',
      cell: (r) => <span className="font-mono tabular-nums text-sm text-muted-foreground">{formatRef(r.reseller_number)}</span>,
    },
    {
      header: t('common.name'),
      cell: (r) => (
        <div className="flex items-center gap-2">
          <Link to={`/resellers/${r.id}`} className="font-medium text-foreground hover:text-primary hover:underline">
            {r.name}
          </Link>
          {current?.id === r.id && (
            <span className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10px] font-mono font-semibold tracking-wider leading-none bg-indigo-600 text-white">
              <Check className="h-2.5 w-2.5" /> {t('reseller.entered')}
            </span>
          )}
        </div>
      ),
    },
    { header: t('reseller.slug'), cell: (r) => <code className="text-xs text-muted-foreground">{r.slug}</code> },
    { header: t('common.language'), cell: (r) => r.default_language.toUpperCase() },
    {
      header: t('common.status'),
      cell: (r) => (
        <Badge variant={r.active ? 'default' : 'secondary'}>
          {r.active ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
    },
    {
      header: t('common.createdAt'),
      cell: (r) => <span className="text-muted-foreground tabular-nums">{new Date(r.created_at).toLocaleDateString()}</span>,
    },
    {
      header: '',
      cell: (r) => (
        <div className="flex items-center gap-2 justify-end">
          <Button asChild size="sm" variant="ghost" className="h-7 text-xs">
            <Link to={`/resellers/${r.id}/settings/features`}>
              <ToggleLeft className="h-3.5 w-3.5" /> {t('reseller.featuresButton')}
            </Link>
          </Button>
          {canEnter && (
            <Button
              type="button"
              onClick={() => handleEnter(r)}
              disabled={!r.active}
              size="sm"
              variant={current?.id === r.id ? 'secondary' : 'default'}
              className={cn('h-7 gap-1.5 text-xs min-w-[5rem] justify-between')}
              aria-label={current?.id === r.id ? r.name : r.name}
            >
              {current?.id === r.id ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  {t('common.entered')}
                </>
              ) : (
                <>
                  <LogIn className="h-3.5 w-3.5" />
                  {t('common.enter')}
                  <ArrowRight className="h-3.5 w-3.5 opacity-70" />
                </>
              )}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow={t('nav.resellers')}
        title={t('reseller.title')}
        description={t('reseller.description')}
        actions={
          <Button asChild>
            <Link to="/resellers/new">
              <Plus className="h-4 w-4" /> {t('reseller.newReseller')}
            </Link>
          </Button>
        }
      />
      {isLoading ? <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
        : error ? <div className="text-sm text-destructive">{(error as Error).message}</div>
        : <DataTable rows={data?.rows ?? []} columns={columns} empty={t('reseller.noResellersYet')} />}
    </>
  );
}
